import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderArchive, Upload, FileText, ExternalLink } from "lucide-react";

const kb = (n) => `${(n / 1024).toFixed(0)} KB`;
const TYPE_LABEL = { supporting: "Bewijsstuk", "z-ticket": "Z-ticket", invoice: "Factuur" };

export default function Documents() {
  const { canWrite } = useAuth();
  const [docs, setDocs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [type, setType] = useState("supporting");
  const [locId, setLocId] = useState("");
  const [desc, setDesc] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); api.get("/locations").then((r) => setLocations(r.data)); }, []);

  const submit = async () => {
    if (!file) return toast.error("Kies een bestand");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", type);
      fd.append("location_id", locId);
      fd.append("description", desc);
      fd.append("reference", ref);
      await api.post("/documents/upload", fd);
      toast.success("Document opgeslagen (bewaartermijn 10 jaar)");
      setOpen(false); setFile(null); setDesc(""); setRef("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openFile = async (id) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/documents/${id}/file`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <FolderArchive className="h-7 w-7 text-primary" /> Bewijsstukken
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Facturen en bewijsstukken · bewaartermijn 10 jaar conform circulaire</p>
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="open-upload"><Upload className="h-4 w-4 mr-2" /> Document uploaden</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Document uploaden</DialogTitle><DialogDescription>Bewaar facturen en bewijsstukken. Bewaartermijn 10 jaar conform de circulaire.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Bestand</Label><Input type="file" data-testid="doc-file" onChange={(e) => setFile(e.target.files[0])} /></div>
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="supporting">Bewijsstuk</SelectItem><SelectItem value="invoice">Factuur</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Vestiging (optioneel)</Label>
                  <Select value={locId} onValueChange={setLocId}>
                    <SelectTrigger data-testid="doc-location"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Referentie / factuurnr.</Label><Input data-testid="doc-ref" value={ref} onChange={(e) => setRef(e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Omschrijving</Label><Input data-testid="doc-desc" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
              </div>
              <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-doc">{saving ? "Bezig…" : "Uploaden"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Bestand</TableHead><TableHead>Type</TableHead><TableHead>Referentie</TableHead><TableHead>Grootte</TableHead><TableHead>Bewaren tot</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nog geen documenten.</TableCell></TableRow>}
              {docs.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" />{d.filename}</TableCell>
                  <TableCell><Badge variant="outline">{TYPE_LABEL[d.doc_type] || d.doc_type}</Badge></TableCell>
                  <TableCell className="text-sm">{d.reference || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{kb(d.size)}</TableCell>
                  <TableCell className="font-mono text-xs">{d.retention_until?.slice(0, 10)}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => openFile(d.id)} data-testid={`view-doc-${d.id}`}><ExternalLink className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
